import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Droplet, Wallet, Landmark, Plus, Trash2, Pencil, X, Save, FileSpreadsheet, FileText } from "lucide-react";
import { api, errMsg, fmtDate, todayISO } from "@/lib/api";
import { Field, StatCard, EmptyState } from "@/components/Field";
import { CAUSALI } from "@/components/patient/PaymentsTab";

export const eur = (v) => (v ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const blank = () => ({ date: todayISO(), amount: "", causale: "", patient_id: "", invoiced: false });

const IncomeForm = ({ patients, editing, onDone }) => {
  const [f, setF] = useState(blank());
  useEffect(() => { setF(editing ? { date: editing.date, amount: editing.amount, causale: editing.causale, patient_id: editing.patient_id || "", invoiced: !!editing.invoiced } : blank()); }, [editing]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, amount: Number(f.amount), patient_id: f.patient_id || null };
      editing ? await api.put(`/incomes/${editing.id}`, body) : await api.post("/incomes", body);
      toast.success(editing ? "Incasso aggiornato" : "Incasso registrato");
      setF(blank());
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="card h-fit space-y-4" data-testid="income-form">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">{editing ? "Modifica incasso" : "Nuovo incasso"}</h3>
        {editing && <button type="button" className="btn-ghost" onClick={() => onDone(true)} data-testid="income-cancel-edit"><X className="h-4 w-4" /></button>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Data"><input type="date" required className="field" value={f.date} onChange={set("date")} data-testid="income-date-input" /></Field>
        <Field label="Importo (€)"><input type="number" step="0.01" min="0" required className="field" value={f.amount} onChange={set("amount")} data-testid="income-amount-input" /></Field>
      </div>
      <Field label="Causale">
        <input required list="causali-admin" className="field" value={f.causale} onChange={set("causale")} placeholder="Es. Bonifico" data-testid="income-causale-input" />
        <datalist id="causali-admin">{CAUSALI.map((c) => <option key={c} value={c} />)}</datalist>
      </Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.invoiced} onChange={(e) => setF({ ...f, invoiced: e.target.checked })} data-testid="income-invoiced-checkbox" /> Con fattura (importo comprensivo di 4% ENPAB)</label>
      <Field label="Paziente (facoltativo)">
        <select className="field" value={f.patient_id} onChange={set("patient_id")} data-testid="income-patient-select">
          <option value="">—</option>
          {patients.map((p) => <option key={p.id} value={p.id}>{p.last_name} {p.first_name}</option>)}
        </select>
      </Field>
      <button className="btn-primary w-full" data-testid="income-save-button"><Plus className="h-4 w-4" /> {editing ? "Aggiorna" : "Registra"}</button>
    </form>
  );
};

const DrawPrice = ({ price, onSaved }) => {
  const [v, setV] = useState(price);
  useEffect(() => { setV(price); }, [price]);
  const save = async () => {
    try {
      await api.put("/settings/billing", { draw_price: Number(v) });
      toast.success("Tariffa prelievo aggiornata");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="flex items-end gap-2">
      <Field label="Tariffa per prelievo (€)"><input type="number" step="0.01" min="0" className="field w-28" value={v} onChange={(e) => setV(e.target.value)} data-testid="draw-price-input" /></Field>
      {Number(v) !== price && <button className="btn-ghost text-[#3d655b]" onClick={save} data-testid="draw-price-save"><Save className="h-4 w-4" /></button>}
    </div>
  );
};

export default function Administration() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [s, setS] = useState(null);
  const [patients, setPatients] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/summary", { params: { month } }).then((r) => setS(r.data)), [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/patients").then((r) => setPatients(r.data)); }, []);

  const remove = async (i) => {
    if (!window.confirm(`Eliminare l'incasso di ${eur(i.amount)}?`)) return;
    await api.delete(`/incomes/${i.id}`);
    load();
  };
  const [exporting, setExporting] = useState("");
  const download = async (format) => {
    setExporting(format);
    try {
      const { data } = await api.get("/admin/export", { params: { month, format }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contabilita-${month}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error("Download non riuscito, riprova");
    } finally {
      setExporting("");
    }
  };
  const label = new Date(`${month}-01T00:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  return (
    <div className="fade-up space-y-8" data-testid="administration-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-xs text-[#3d655b]">Amministrazione</div>
          <h1 className="page-title mt-1">Contabilità di {label}</h1>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Mese"><input type="month" className="field w-auto" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="admin-month-input" /></Field>
          <button className="btn-outline" disabled={!!exporting} onClick={() => download("xlsx")} data-testid="admin-export-xlsx"><FileSpreadsheet className="h-4 w-4" /> {exporting === "xlsx" ? "Preparazione…" : "Excel"}</button>
          <button className="btn-primary" disabled={!!exporting} onClick={() => download("pdf")} data-testid="admin-export-pdf"><FileText className="h-4 w-4" /> {exporting === "pdf" ? "Preparazione…" : "PDF"}</button>
        </div>
      </div>
      {s && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard label={`Prelievi da fatturare (${s.draws_count} × ${eur(s.draw_price)})`} value={eur(s.draws_total)} icon={Droplet} testid="admin-draws-total" />
            <StatCard label={`Incassi visite (${s.incomes.length})`} value={eur(s.incomes_total)} icon={Wallet} testid="admin-incomes-total" />
            <StatCard label="Totale generale del mese" value={eur(s.grand_total)} icon={Landmark} testid="admin-grand-total" dark />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="card" data-testid="admin-invoiced-card">
              <div className="label-xs">Incassi visite fatturate ({s.incomes.filter((i) => i.invoiced).length})</div>
              <div className="mt-3 text-3xl font-extrabold tracking-tight" data-testid="admin-invoiced-total">{eur(s.invoiced_total)}</div>
              <div className="mt-2 text-xs text-slate-500">Netto {eur(s.invoiced_net)} · cassa ENPAB 4% <span data-testid="admin-enpab-total">{eur(s.enpab_total)}</span></div>
            </div>
            <div className="card" data-testid="admin-noinvoice-card">
              <div className="label-xs">Incassi visite non fatturate ({s.incomes.filter((i) => !i.invoiced).length})</div>
              <div className="mt-3 text-3xl font-extrabold tracking-tight" data-testid="admin-noinvoice-total">{eur(s.noinvoice_total)}</div>
              <div className="mt-2 text-xs text-slate-500">Importi netti, senza fattura né cassa</div>
            </div>
          </div>

          <div className="card space-y-4" data-testid="admin-draws-section">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold">Prelievi da fatturare</h2>
                <p className="text-sm text-slate-500">Calcolati automaticamente dal registro prelievi del mese.</p>
              </div>
              <DrawPrice price={s.draw_price} onSaved={load} />
            </div>
            {s.draws_by_sede.length ? (
              <table className="w-full text-left text-sm" data-testid="admin-draws-table">
                <thead><tr><th className="label-xs py-2">Sede</th><th className="label-xs py-2 text-right">Prelievi</th><th className="label-xs py-2 text-right">Importo</th></tr></thead>
                <tbody>
                  {s.draws_by_sede.map((d) => (
                    <tr key={d.sede} className="border-t border-[#e2e8f0]"><td className="py-2.5 font-medium">{d.sede}</td><td className="py-2.5 text-right">{d.count}</td><td className="py-2.5 text-right font-bold">{eur(d.total)}</td></tr>
                  ))}
                  <tr className="border-t-2 border-[#3d655b]"><td className="py-2.5 font-bold">Totale</td><td className="py-2.5 text-right font-bold">{s.draws_count}</td><td className="py-2.5 text-right text-base font-extrabold text-[#3d655b]">{eur(s.draws_total)}</td></tr>
                </tbody>
              </table>
            ) : <p className="text-sm text-slate-400">Nessun prelievo registrato in questo mese.</p>}
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <IncomeForm patients={patients} editing={editing} onDone={() => { setEditing(null); load(); }} />
            <div className="space-y-4 lg:col-span-2">
              {s.incomes_by_causale.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="admin-causale-totals">
                  {s.incomes_by_causale.map((c) => (
                    <span key={c.causale} className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold shadow-sm">{c.causale}: <span className="text-[#3d655b]">{eur(c.total)}</span></span>
                  ))}
                </div>
              )}
              {s.incomes.length ? (
                <div className="overflow-x-auto rounded-2xl border border-[#e2e8f0] bg-white">
                  <table className="w-full min-w-[560px] text-left text-sm" data-testid="admin-incomes-table">
                    <thead className="bg-[#f8fafc]"><tr>
                      <th className="label-xs px-4 py-3">Data</th><th className="label-xs px-4 py-3">Paziente</th>
                      <th className="label-xs px-4 py-3">Causale</th><th className="label-xs px-4 py-3 text-right">Importo</th><th />
                    </tr></thead>
                    <tbody>
                      {s.incomes.map((i) => (
                        <tr key={i.id} className="border-t border-[#e2e8f0]" data-testid={`income-row-${i.id}`}>
                          <td className="px-4 py-3">{fmtDate(i.date)}</td>
                          <td className="px-4 py-3 font-medium">{i.patient_name || "—"}{i.visit_id && <span className="ml-2 rounded-full bg-[#edf2ef] px-2 py-0.5 text-[10px] font-semibold text-[#2f4f47]">visita</span>}</td>
                          <td className="px-4 py-3">{i.causale}{i.tariff && <div className="text-[11px] text-slate-500">{i.tariff}</div>}<span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${i.invoiced ? "bg-[#edf2ef] text-[#2f4f47]" : "bg-slate-100 text-slate-500"}`} data-testid={`income-invoiced-${i.id}`}>{i.invoiced ? "Fatturata" : "Non fatturata"}</span></td>
                          <td className="px-4 py-3 text-right font-bold">{eur(i.amount)}</td>
                          <td className="whitespace-nowrap px-2 text-right">
                            <button className="btn-ghost" onClick={() => setEditing(i)} data-testid={`income-edit-${i.id}`}><Pencil className="h-4 w-4" /></button>
                            <button className="btn-ghost hover:text-rose-600" onClick={() => remove(i)} data-testid={`income-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-[#3d655b]"><td colSpan={3} className="px-4 py-3 font-bold">Totale incassi visite</td><td className="px-4 py-3 text-right text-base font-extrabold text-[#3d655b]">{eur(s.incomes_total)}</td><td /></tr>
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState icon={Wallet} title="Nessun incasso in questo mese" text="Gli incassi inseriti alla registrazione della visita compaiono qui automaticamente." testid="admin-incomes-empty" />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
